/**
 * This module contains generic code to communicate with the signoxe API.
 */

 import { normalize } from "normalizr";
 import { actions as notifActions } from "redux-notifications";
 import { fetchAll, fetchAllAssets } from "signoxe/actions/fetchAll";
 import {
   ASSETS_RESOURCE,
   IMAGE_RESOURCE,
   AUDIO_RESOURCE,
   SCHEMAS,
   VIDEO_RESOURCE,
   PDF_RESOURCE,
   ZIP_RESOURCE,
   CAMPAIGN_RESOURCE,
   PLAYLISTS_RESOURCE,
   PLAYLIST_ITEMS_RESOURCE,
   PLAYLISTS_RESOURCE_V2,
 } from "signoxe/api/resources";
 import { asset } from "signoxe/api/schema";
 import {
   DELETE_RESOURCE_FAILURE,
   DELETE_RESOURCE_REQUEST,
   DELETE_RESOURCE_SUCCESS,
   FETCH_RESOURCE_FAILURE,
   FETCH_RESOURCE_REQUEST,
   FETCH_RESOURCE_SUCCESS,
   LOGIN_LOGOUT,
   PATCH_RESOURCE_FAILURE,
   PATCH_RESOURCE_REQUEST,
   PATCH_RESOURCE_SUCCESS,
   POST_RESOURCE_FAILURE,
   POST_RESOURCE_REQUEST,
   POST_RESOURCE_SUCCESS,
   PUT_RESOURCE_FAILURE,
   PUT_RESOURCE_REQUEST,
   PUT_RESOURCE_SUCCESS,
   SHOW_UPLOADS_TRAY,
   UPLOAD_ASSET_FAILURE,
   UPLOAD_ASSET_PROGRESS,
   UPLOAD_ASSET_REQUEST,
   UPLOAD_ASSET_SUCCESS,
   UPDATE_NEXT,
   FETCH_PLAYLIST_SUCCESS
 } from "signoxe/constants/actionTypes";
 import {
   API_ROOT,
   DEVICES_REPORT,
   DEVICE_REPORT,
 } from "signoxe/constants/config";
 import request from "superagent";

 const { notifSend } = notifActions;
 
 /**
  * Creates the header containing the authentication token for the currently logged in user.
  */
 export const getAuthTokenHeader = () =>
   `Bearer ${localStorage.getItem("token")}`;
 
 /**
  * This curried function fetches the specified resource from the API and dispatches the normalised
  * result if successful.
  *
  * If the refresh parameter is set to true it will signal the reducer that the current data should
  * be replaced with newly fetched data. Otherwise the reducer will update the existing data.
  *
  * @param {string} resource The resource to fetch
  * @param {bool} single Whether the resource being fetched is a list or a single item.
  */



 export const fetchResource =
   (resource, single = false, url = null) =>
   (refresh, resourceID) =>
   (dispatch) => {
     dispatch({ type: FETCH_RESOURCE_REQUEST, resource });
     let default_url = !url ? `${API_ROOT}${resource}/` : url;
 
     const schema = single ? SCHEMAS[resource] : [SCHEMAS[resource]];
     if (single && resourceID) {
       default_url = `${API_ROOT}${resource}/${resourceID}`;
     }
     
     const pagedResources = [ "device_groups", "playlistsV2"];
 
     return request
       .get(default_url)
       .set("Authorization", getAuthTokenHeader())
       .accept("application/json")
       .then((response) => {
         let data = response.body;
         
         if(resource === 'playlists' && single){               
          dispatch({
            type: FETCH_PLAYLIST_SUCCESS,
            payload: response.body,
          });

        }
 
         if(resource==='auth/me'){
           localStorage.setItem('alias', data.profile.alias);
           localStorage.setItem('max_uploadable_asset_size', data['max_uploadable_asset_size']);
         }
 
         if (pagedResources.includes(resource)) {
           data = data.results;
           dispatch({
             type: UPDATE_NEXT,
             next: response.body.next,
             resource,
           });
         }


         return normalize(data, schema);
       })
       .then(
         (response) => {
           dispatch({
             type: FETCH_RESOURCE_SUCCESS,
             resource,
             response,
             refresh,
             single,
           });
         },
         (error) => {
           console.log("Fetch resource resource + error = ", resource, error);
           if (error.status === 401) {
             dispatch({ type: LOGIN_LOGOUT });
           } else {
             dispatch({
               type: FETCH_RESOURCE_FAILURE,
               resource,
               error: error.message || "Something went wrong.",
             });
           }
         }
       );
   };
 
 /**
  * This curried function sends a patch request to update data for the specified resource.
  *
  * The resourceId parameter takes the id of the object to update, and the data is the data to send
  * to the server.
  *
  * @param {string} resource The resource to update
  * @param {string} subResource The subresource to update
  * @param {bool} noId Should the API endpoint include the id of object being updated.
  */
 
 export const updateResource =
   (resource, subResource = null, noId = false) =>
   (resourceId, data) =>
   (dispatch) => {
     dispatch({ type: PATCH_RESOURCE_REQUEST, resource, resourceId });
 
     const subResourcePath = subResource === null ? "" : `${subResource}/`;
     const id = noId ? "" : `${resourceId}/`;
 
     return request
       .patch(`${API_ROOT}${resource}/${id}${subResourcePath}`)
       .set("Authorization", getAuthTokenHeader())
       .accept("application/json")
       .send(data)
       .then((response) => normalize(response.body, SCHEMAS[resource]))
       .then(
         (response) => {
           dispatch({
             type: PATCH_RESOURCE_SUCCESS,
             resource,
             resourceId,
             response,
           });
 
           console.log("Campaign Update success data = ", data);
           if (data.campaign === null) {
             dispatch(
               notifSend({
                 message: "Asset has been removed succesfully",
                 kind: "success",
                 dismissAfter: 5000,
               })
             );
           }
           if(resource == 'channels'){
            dispatch(
              notifSend({
                message: "Update was succesful",
                kind: "success",
                dismissAfter: 5000,
              })
            );
          }
         },
         (error) => {
           const { response } = error;
 
           dispatch({
             type: PATCH_RESOURCE_FAILURE,
             resource,
             resourceId,
             error: error.message || "Something went wrong.",
           });
 
           if (response && response.statusCode === 403) {
             dispatch(
               notifSend({
                 message: "User does not have this permission.",
                 kind: "alert",
                 dismissAfter: 5000,
               })
             );
             return;
           }
 
           dispatch(
             notifSend({
               message: "Unable to update. Check network connection.",
               kind: "alert",
               dismissAfter: 5000,
             })
           );
         }
       )
       .then(() => {
        dispatch(fetchResource(resource)(true));
       });
   };
 
export const updateImgResource =
  (resource, subResource = null, noId = false) =>
  (resourceId, data) =>
  (dispatch) => {
    dispatch({ type: PATCH_RESOURCE_REQUEST, resource, resourceId });

    const subResourcePath = subResource === null ? "" : `${subResource}/`;
    const id = noId ? "" : `${resourceId}/`;

    const formData = new FormData();

    // Append non-file data to FormData
    Object.keys(data).forEach(key => {
        formData.append(key, data[key]);
    });

    return request
      .patch(`${API_ROOT}${resource}/${id}${subResourcePath}`)
      .set("Authorization", getAuthTokenHeader())
      .accept("application/json")
      .send(formData) // Use formData instead of data
      .then((response) => normalize(response.body, SCHEMAS[resource]))
      .then(
        (response) => {
          dispatch({
            type: PATCH_RESOURCE_SUCCESS,
            resource,
            resourceId,
            response,
          });

          console.log("Campaign Update success data = ", data);
          if (data.campaign === null) {
            dispatch(
              notifSend({
                message: "Asset has been removed successfully",
                kind: "success",
                dismissAfter: 5000,
              })
            );
          }
          if (resource === 'channels') {
            dispatch(
              notifSend({
                message: "Update was successful",
                kind: "success",
                dismissAfter: 5000,
              })
            );
          }
        },
        (error) => {
          const { response } = error;

          dispatch({
            type: PATCH_RESOURCE_FAILURE,
            resource,
            resourceId,
            error: error.message || "Something went wrong.",
          });

          if (response && response.statusCode === 403) {
            dispatch(
              notifSend({
                message: "User does not have this permission.",
                kind: "alert",
                dismissAfter: 5000,
              })
            );
            return;
          }

          dispatch(
            notifSend({
              message: "Unable to update. Check network connection.",
              kind: "alert",
              dismissAfter: 5000,
            })
          );
        }
      )
      .then(() => {
        dispatch(fetchResource(resource)(true));
      });
  };

/**
 * This curried function sends a post request to create a new object of the specified resource.
 *
 * @param {string} resource The resource to create
 * @param {string} subResource The subresource to create
 * @param {object} messages An object with a success and error attribute containing success
 *                          and error messages
 * @param {string} route One of 'list' or 'detail' to specify whether the subresource is applies
 *                       to a single object ('detail') or to the entire set ('list').
 */
export const addResource =
  (
    resource,
    subResource,
    messages = {
      success: null,
      error: null,
    },
    route = "detail"
  ) =>
  (data) =>
  (dispatch) => {
    dispatch({ type: POST_RESOURCE_REQUEST, resource, data });

    let resourcePath = `${API_ROOT}${resource}/`;
    let schema = SCHEMAS[resource];

    if (subResource !== undefined) {
      if (route === "detail") {
        resourcePath += `${data.id}/${subResource}/`;
      } else if (route === "list") {
        resourcePath += `${subResource}/`;
        schema = [schema];
      }
    }

    const postRequest = request.post(resourcePath);

    return postRequest
      .set("Authorization", getAuthTokenHeader())
      .send(data)
      .accept("application/json")
      .then((response) => normalize(response.body, schema))
      .then(
        (response) => {
          dispatch({
            type: POST_RESOURCE_SUCCESS,
            resource,
            response,
          });
          if (messages.success) {
            dispatch(
              notifSend({
                message: messages.success,
                kind: "success",
                dismissAfter: 5000,
              })
            );
          }
        },
        (error) => {
          const { response } = error;
          dispatch({
            type: POST_RESOURCE_FAILURE,
            resource,
            error: error.message || "Something went wrong.",
          });
          if (response && response.statusCode === 403) {
            dispatch(
              notifSend({
                message: "User does not have this permission.",
                kind: "alert",
                dismissAfter: 5000,
              })
            );
            return;
          }

          if (messages.error) {
            dispatch(
              notifSend({
                message:
                  messages.error ||
                  "Error connecting to website. Check network connection.",
                kind: "alert",
                dismissAfter: 5000,
              })
            );
          }
        }
      )
      .then(() => {
        // pass
        processPlaylistItem(resource, dispatch);
       })
       .then(() => dispatch(fetchResource(resource)(true))
      );
  };


export const uploadResourceMedia =
  (
    resource,
    subResource,
    messages = {
      success: null,
      error: null,
    },
    route = "detail"
  ) =>
  (data) =>
  (dispatch) => {
    dispatch({ type: POST_RESOURCE_REQUEST, resource, data });
  
    let resourcePath = `${API_ROOT}${resource}/`;
    let schema = SCHEMAS[resource];
  
    if (subResource !== undefined) {
      if (route === "detail") {
        resourcePath += `${data.id}/${subResource}/`;
      } else if (route === "list") {
        resourcePath += `${subResource}/`;
        schema = [schema];
      }
    }
  
    const postRequest = request.post(resourcePath);
  
    return postRequest
      .set("Authorization", getAuthTokenHeader())
      .attach("media_file", data.device_logo)
      .accept("application/json")
      .then((response) => normalize(response.body, schema))
      .then(
        (response) => {
          dispatch({
            type: POST_RESOURCE_SUCCESS,
            resource,
            response,
          });
          if (messages.success) {
            dispatch(
              notifSend({
                message: messages.success,
                kind: "success",
                dismissAfter: 5000,
              })
            );
          }
        },
        (error) => {
          const { response } = error;
          dispatch({
            type: POST_RESOURCE_FAILURE,
            resource,
            error: error.message || "Something went wrong.",
          });
          if (response && response.statusCode === 403) {
            dispatch(
              notifSend({
                message: "User does not have this permission.",
                kind: "alert",
                dismissAfter: 5000,
              })
            );
            return;
          }
  
          if (messages.error) {
            dispatch(
              notifSend({
                message:
                  messages.error ||
                  "Error connecting to website. Check network connection.",
                kind: "alert",
                dismissAfter: 5000,
              })
            );
          }
        }
      )
      .then(() => dispatch(fetchResource(resource)(true)));
  };
  
  
/**
 * This curried function sends a put request to create or replace an new object
 * of the specified resource.
 */
export const putResource =
  ({ resource, subResource, route, refreshAll }) =>
  (id, data) =>
  (dispatch) => {
    dispatch({ type: PUT_RESOURCE_REQUEST, resource, data });

    let resourcePath = `${API_ROOT}${resource}/`;
    let schema = SCHEMAS[resource];

    if (subResource !== undefined) {
      if (route === "detail") {
        resourcePath += `${id}/${subResource}/`;
      } else if (route === "list") {
        resourcePath += `${subResource}/`;
        schema = [schema];
      }
    }

    return request
      .put(resourcePath)
      .set("Authorization", getAuthTokenHeader())
      .send(data)
      .accept("application/json")
      .then((response) => normalize(response.body, schema))
      .then(
        (response) => {
          dispatch({
            type: PUT_RESOURCE_SUCCESS,
            resource,
            response,
          });
        },
        (error) => {
          const { error } = response;

          dispatch({
            type: PUT_RESOURCE_FAILURE,
            resource,
            error: error.message || "Something went wrong.",
          });
          if (response && response.statusCode === 403) {
            dispatch(
              notifSend({
                message: "User does not have this permission.",
                kind: "alert",
                dismissAfter: 5000,
              })
            );
            return;
          }
          if (messages.error) {
            dispatch(
              notifSend({
                message:
                  messages.error ||
                  "Error connecting to website. Check network connection.",
                kind: "alert",
                dismissAfter: 5000,
              })
            );
          }
        }
      )
      .then(() =>
      refreshAll
        ? dispatch(fetchAll())
        : dispatch(fetchResource(resource)(true)))
  };

/**
 * Sends a delete request to delete the specified resource object.
 *
 * The resourceId specifies the id of the object to delete.
 *
 * @param {string} resource The resource to delete
 * @param {string} subResource The subresource to delete
 */

const processPlaylistItem =  (resource, dispatch) => {
  const playlistId = localStorage.getItem("playlist_id");
  if (resource === PLAYLIST_ITEMS_RESOURCE) {
    dispatch(fetchResource(PLAYLISTS_RESOURCE, true)(true, playlistId));
  }
};


export const deleteResource =
  (resource, subResource = null) =>
  (resourceId) =>
  (dispatch) => {
    dispatch({
      type: DELETE_RESOURCE_REQUEST,
      resource,
      subResource,
      resourceId,
    });

    const subResourcePath = subResource === null ? "" : `${subResource}/`;

    return request
      .del(`${API_ROOT}${resource}/${resourceId}/${subResourcePath}`)
      .set("Authorization", getAuthTokenHeader())
      .accept("application/json")
      .send()
      .then(
        (response) => {
          dispatch({
            type: DELETE_RESOURCE_SUCCESS,
            resource,
            subResource,
            resourceId,
            response,
          });

          if (resource === CAMPAIGN_RESOURCE) {
            dispatch(
              notifSend({
                message: "Campaign Deleted",
                kind: "success",
                dismissAfter: 5000,
              })
            );
          }

          if (resource === ASSETS_RESOURCE) {
            dispatch(
              notifSend({
                message: "Asset Deleted",
                kind: "success",
                dismissAfter: 5000,
              })
            );
          }
        },
        (error) => {
          const { response } = error;
          dispatch({
            type: DELETE_RESOURCE_FAILURE,
            resource,
            subResource,
            resourceId,
            error: error.message || "Something went wrong.",
          });
          if (response && response.statusCode === 403) {
            dispatch(
              notifSend({
                message: "User does not have this permission.",
                kind: "alert",
                dismissAfter: 5000,
              })
            );
            return;
          }
          dispatch(
            notifSend({
              message: "Error connecting to website. Check network connection.",
              kind: "alert",
              dismissAfter: 5000,
            })
          );
        }
      )
      .then(() => {
       // pass
       processPlaylistItem(resource, dispatch);
      })
      .then(() => dispatch(fetchResource(resource)(true))
    );
  };

export const saveRegionFile = (file_type, file, id) => (dispatch) => {
  if (file.size / 1024 / 1024 > 25) {
    const errorMessage = `
    Error in uploading file '${file.name}'.
    File limit size exceeded, try files below 25MB`;
    dispatch(
      notifSend({
        message: errorMessage,
        kind: "alert",
        dismissAfter: 5000,
      })
    );
    return;
  }

  const resource = "region_upload";

  dispatch({ type: UPLOAD_ASSET_REQUEST, file, resource });
  dispatch({ type: SHOW_UPLOADS_TRAY });

  return request
    .patch(`${API_ROOT}${resource}/${id}/`)
    .set("Authorization", getAuthTokenHeader())
    .accept("application/json")
    .attach(file_type, file)
    .on("progress", (ev) =>
      dispatch({
        type: UPLOAD_ASSET_PROGRESS,
        progress: ev.percent,
        file: file,
      })
    )
    .on("error", (error) =>
      dispatch({
        type: UPLOAD_ASSET_FAILURE,
        file: file,
        resource,
        error: error.message || "Something went wrong.",
      })
    )
    .end((err, res) => {
      if (!err && res.ok) {
        dispatch({
          type: UPLOAD_ASSET_SUCCESS,
          resource,
          file: file,
          response: normalize(res.body, asset),
        });
      } else {
        dispatch({
          type: UPLOAD_ASSET_FAILURE,
          file: file,
          resource,
          error: err.response || "Something went wrong.",
        });

        if (res.statusCode === 403) {
          dispatch(
            notifSend({
              message: "User does not have this permission.",
              kind: "alert",
              dismissAfter: 5000,
            })
          );
          return;
        }

        dispatch(
          notifSend({
            message: "Error uploading. Check network connection.",
            kind: "alert",
            dismissAfter: 5000,
          })
        );
      }
    });
};

/**
 * Specialised function for uploading assets to the server.
 * @param file File to upload
 */
export const uploadAsset =
  (file, selectedCampaign = null, assetname = null) =>
  (dispatch) => {
    let uploadResource;
    const username = localStorage.getItem('username')

    if (file.type.startsWith("image")) {
      uploadResource = IMAGE_RESOURCE;
    } else if (file.type.startsWith("video")) {
      uploadResource = VIDEO_RESOURCE;
    } else if (file.type.startsWith("audio")) {
     uploadResource = AUDIO_RESOURCE;
   } else if (file.type === "application/pdf") {
      uploadResource = PDF_RESOURCE;
    } else if (file.type.indexOf("officedocument") !== -1) {
      uploadResource = PDF_RESOURCE;
    } else if (file.type.indexOf("msword") !== -1) {
      uploadResource = PDF_RESOURCE;
    } else if (file.type.indexOf("excel") !== -1) {
      uploadResource = PDF_RESOURCE;
    } else if (file.type.indexOf("powerpoint") !== -1) {
      uploadResource = PDF_RESOURCE;
    } else if (file.name.endsWith(".zip")) {
      uploadResource = ZIP_RESOURCE;
    }

    if (uploadResource === PDF_RESOURCE) {
      if (file.size / 1024 / 1024 > 10) {
        const errorMessage = `
      Error in uploading file '${file.name}'.
      File limit size exceeded, try files below 10MB`;
        dispatch(
          notifSend({
            message: errorMessage,
            kind: "alert",
            dismissAfter: 5000,
          })
        );
        return;
      }
    }


    const resource = ASSETS_RESOURCE;

    dispatch({ type: UPLOAD_ASSET_REQUEST, file, resource });
    dispatch({ type: SHOW_UPLOADS_TRAY });

    if(file.name.endsWith(".zip")) {
      return request
      .post(`${API_ROOT}${uploadResource}/`)
      .set("Authorization", getAuthTokenHeader())
      .accept("application/json")
      .attach("media_file", file)
      .field("name", assetname || file.name)
      .field("uploaded_by", username)
      .on("progress", (ev) =>
        dispatch({
          type: UPLOAD_ASSET_PROGRESS,
          progress: ev.percent,
          file: file,
        })
      )
      .on("error", (error) =>
        dispatch({
          type: UPLOAD_ASSET_FAILURE,
          file: file,
          resource,
          error: error.message || "Something went wrong.",
        })
      )
      .end((err, res) => {
        if (!err && res.ok) {
          dispatch({
            type: UPLOAD_ASSET_SUCCESS,
            resource,
            file: file,
            response: normalize(res.body, asset),
          });
        } else {
          dispatch({
            type: UPLOAD_ASSET_FAILURE,
            file: file,
            resource,
            error: err.response || "Something went wrong.",
          });

          if (res.statusCode === 403) {
            dispatch(
              notifSend({
                message: "User does not have this permission.",
                kind: "alert",
                dismissAfter: 5000,
              })
            );
            return;
          }

          dispatch(
            notifSend({
              message: "Error uploading. Check network connection.",
              kind: "alert",
              dismissAfter: 5000,
            })
          );
        }
      });} else {
      return request
      .post(`${API_ROOT}${uploadResource}/`)
      .set("Authorization", getAuthTokenHeader())
      .accept("application/json")
      .attach("media_file", file)
      .field("name", assetname || file.name)
      .field("uploaded_by", username)
      .field("campaign", selectedCampaign ? selectedCampaign.id : '')
      .on("progress", (ev) =>
        dispatch({
          type: UPLOAD_ASSET_PROGRESS,
          progress: ev.percent,
          file: file,
        })
      )
      .on("error", (error) =>
        dispatch({
          type: UPLOAD_ASSET_FAILURE,
          file: file,
          resource,
          error: error.message || "Something went wrong.",
        })
      )
      .end((err, res) => {
        if (!err && res.ok) {         
          dispatch({
            type: UPLOAD_ASSET_SUCCESS,
            resource,
            file: file,
            response: normalize(res.body, asset),
          });
          dispatch(fetchAllAssets());   
          console.log("ran ")
        
        } else {
          dispatch({
            type: UPLOAD_ASSET_FAILURE,
            file: file,
            resource,
            error: err.response || "Something went wrong.",
          });

          if (res.statusCode === 403) {
            dispatch(
              notifSend({
                message: "User does not have this permission.",
                kind: "alert",
                dismissAfter: 5000,
              })
            );
            return;
          }

          dispatch(
            notifSend({
              message: "Error uploading. Check network connection.",
              kind: "alert",
              dismissAfter: 5000,
            })
          );
        }
      });
      }
  };

export const uploadZip = (resource) => (payload) => (dispatch) => {
  const { media_file, name, tags, data, asset_url } = payload;

  dispatch({ type: UPLOAD_ASSET_REQUEST, file: media_file, resource });
  dispatch({ type: SHOW_UPLOADS_TRAY });

  return request
    .post(`${API_ROOT}${resource}/`)
    .set("Authorization", getAuthTokenHeader())
    .accept("application/json")
    .attach("media_file", media_file)
    .field("name", name)
    .field("extra_data", data)
    .field("asset_url", asset_url)
    .field("tags", tags)
    .on("progress", (ev) => {
      dispatch({
        type: UPLOAD_ASSET_PROGRESS,
        progress: ev.percent,
        file: media_file,
      });
    })
    .on("error", (error) => {
      console.log("Error occured during upload...", error);
      dispatch({
        type: UPLOAD_ASSET_FAILURE,
        file: media_file,
        resource,
        error: error.message || "Something went wrong.",
      });
    })
    .end((err, res) => {
      if (!err && res.ok) {
        dispatch(
          notifSend({
            message: "Zip asset has been saved.",
            kind: "success",
            dismissAfter: 5000,
          })
        );
        dispatch({
          type: UPLOAD_ASSET_SUCCESS,
          resource,
          file: media_file,
          response: normalize(res.body, asset),
        });
      } else {
        dispatch({
          type: UPLOAD_ASSET_FAILURE,
          file: media_file,
          resource,
          error: err.response || "Something went wrong.",
        });

        if (res.statusCode === 403) {
          dispatch(
            notifSend({
              message: "User does not have this permission.",
              kind: "alert",
              dismissAfter: 5000,
            })
          );
          return;
        }

        dispatch(
          notifSend({
            message: "Error uploading. Check network connection.",
            kind: "alert",
            dismissAfter: 5000,
          })
        );
      }
    });
};

export const downloadReport = (deviceID) => (dispatch) => {
  const url = `${DEVICE_REPORT}/${deviceID}`;
  const options = {
    headers: {
      Authorization: getAuthTokenHeader(),
    },
  };
  fetch(url, options)
    .then((response) => {
      return response.blob();
    })
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "report.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
    })
    .catch((err) => {
      alert("No Report exists for this device.");
    });
};

export const mailActiveHoursReports = (data, download_state) => (dispatch) => {
const url = `${DEVICES_REPORT}/mail-device-active-hours-report/`;
const options = {
  method: "POST",
  headers: {
    Authorization: getAuthTokenHeader(),
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  body: JSON.stringify(data),
};
fetch(url, options)
  .then((response) => {
    console.log("response: " + response)
    download_state(false)
  })
  .catch((err) => {
    alert("No Report exists for this device.");
    download_state(false)

  });
};
export const downloadActiveHoursReports = (data, download_state) => (dispatch) => {
 const url = `${DEVICES_REPORT}/download-device-active-hours-report/`;
 const options = {
   method: "POST",
   headers: {
     Authorization: getAuthTokenHeader(),
     Accept: "application/json",
     "Content-Type": "application/json",
   },
   body: JSON.stringify(data),
 };
 fetch(url, options)
   .then((response) => {
     return response.blob();
   })
   .then((blob) => {
     const url = window.URL.createObjectURL(blob);
     const a = document.createElement("a");
     a.href = url;
     a.download = "Active Hours Report.csv";
     document.body.appendChild(a);
     a.click();
     a.remove();
     download_state(false)
   })
   .catch((err) => {
     alert("No Report exists for this device.");
     download_state(false)

   });
};

export const mailReports = (data, reportType, download_state) => (dispatch) => {
const url =
  reportType === "device"
    ? `${DEVICES_REPORT}/mail-report`
    : `${DEVICES_REPORT}/mail-report-campaign`;
const options = {
  method: "POST",
  headers: {
    Authorization: getAuthTokenHeader(),
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  body: JSON.stringify(data),
};
fetch(url, options)
  .then((response) => {
    console.log("response success", response);
    download_state(false)

  })
  
  .catch((err) => {
    alert("No Report exists for this device.", err);
    download_state(false)

  });
};



export const downloadReports = (data, reportType, download_state) => (dispatch) => {
 const url =
   reportType === "device"
     ? `${DEVICES_REPORT}/download-report`
     : `${DEVICES_REPORT}/download-report-campaign`;
 const options = {
   method: "POST",
   headers: {
     Authorization: getAuthTokenHeader(),
     Accept: "application/json",
     "Content-Type": "application/json",
   },
   body: JSON.stringify(data),
 };
 fetch(url, options)
   .then((response) => {
     return response.blob();
   })
   .then((blob) => {
     const url = window.URL.createObjectURL(blob);

     const a = document.createElement("a");
     a.href = url;
     a.download = "Playback Report.csv";
     document.body.appendChild(a);
     a.click();
     a.remove();
     download_state(false)
   })
   .catch((err) => {
      console.log("errr--- ", err)
     alert("No Report exists for this device -- ", err);
     download_state(false)

   });
};

export const groupOperationalHours = (data, callback) => (dispatch) => {
 const url = `${DEVICES_REPORT}/group-operational-hours/`;
 const options = {
   method: "POST",
   headers: {
     Authorization: getAuthTokenHeader(),
     Accept: "application/json",
     "Content-Type": "application/json",
   },
   body: JSON.stringify(data),
 };
 fetch(url, options)
   .then((response) => {
     if (response.status == 400) {
       alert("Invalid Operational hours");
       callback(400)
     } else {
       callback(200)
     }
   })
   .catch((err) => {
     alert("No devices exist in this group.");
   });
};

export const userReportsDownload = (data) => (dispatch) => {
 const url = `${API_ROOT}user_report_download/`;
 const options = {
   method: "POST",
   headers: {
     Authorization: getAuthTokenHeader(),
     Accept: "application/json",
     "Content-Type": "application/json",
   },
   body: JSON.stringify(data),
 };
 fetch(url, options)
   .then((response) => {
     return response.blob();
   })
   .then((blob) => {
     const url = window.URL.createObjectURL(blob);

     const a = document.createElement("a");
     a.href = url;
     a.download = "User Report.csv";
     document.body.appendChild(a);
     a.click();
     a.remove();
   })
   .catch((err) => {
     alert(err);
   });
};

export const fetchPingDevices = (data) => {
 const url = `${API_ROOT}ping-devices/`;
 const options = {
   method: 'POST',
   headers: {
     Authorization: getAuthTokenHeader(),
     Accept: 'application/json',
     'Content-Type': 'application/json',
   },
   body: JSON.stringify(data),
 };

 return fetch(url, options)
   .then((response) => {
     console.log("response ping", response);
     if (!response.ok) {
       throw new Error(`HTTP error! Status: ${response.status}`);
     }
     return response.json();
   })
   .catch((err) => {
     console.log('Error fetching ping devices:', err);
     throw err; // You can choose to throw the error here or handle it differently
   });
};


export const exportPingDevicestoCSV = (data) => {
 const url = `${API_ROOT}ping-devices/export/`;
 const options = {
   method: 'POST',
   headers: {
     Authorization: getAuthTokenHeader(),
     Accept: 'application/json',
     'Content-Type': 'application/json',
   },
   body: JSON.stringify(data),
 };

 return fetch(url, options)
 .then((response) => {
   return response.blob();
 })
 .then((blob) => {
   const url = window.URL.createObjectURL(blob);

   const a = document.createElement("a");
   a.href = url;
   a.download = "Ping Devices.csv";
   document.body.appendChild(a);
   a.click();
   a.remove();
 })
 .catch((err) => {
   alert(err);
 });
};






// WEBPACK FOOTER //
// ./node_modules/source-map-loader!./src/actions/remote.js